import type { ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { SessionProvider, useSession } from '@/auth/SessionContext';
import { PwaThemeProvider } from '@/context/PwaThemeContext';
import { PwaRouteSync } from '@/components/PwaRouteSync';
import { useLockAppViewportHeight } from '@/hooks/useLockAppViewportHeight';
import { AppShell } from '@/pages/AppShell';
import { BookingsListPage } from '@/pages/BookingsListPage';
import { BookingsSearchPage } from '@/pages/BookingsSearchPage';
import { CalendarPage } from '@/pages/CalendarPage';
import { ChatsLayout } from '@/pages/ChatsLayout';
import { ConversationListPage } from '@/pages/ConversationListPage';
import { ConversationThreadPage } from '@/pages/ConversationThreadPage';
import { AgentResourcesPage } from '@/pages/AgentResourcesPage';
import { AgentRulesPage } from '@/pages/AgentRulesPage';
import { HomePage } from '@/pages/HomePage';
import { LoginPage } from '@/pages/LoginPage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { RequirePwaPermission } from '@/auth/RequirePwaPermission';

function RequireAuth({ children }: { children: ReactNode }) {
  const { jwt, opaque, bootstrapping } = useSession();
  if (bootstrapping) {
    return <p className="pwa-muted pwa-boot">Restaurando sesión…</p>;
  }
  if (!jwt && !opaque) return <Navigate to="/login" replace />;
  if (!jwt) {
    return <p className="pwa-muted pwa-boot">Restaurando sesión…</p>;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<RequirePwaPermission path="/"><HomePage /></RequirePwaPermission>} />
        <Route
          path="agents/:agentId/recursos"
          element={
            <RequirePwaPermission path="/calendar">
              <AgentResourcesPage />
            </RequirePwaPermission>
          }
        />
        <Route
          path="agents/:agentId/reglas"
          element={
            <RequirePwaPermission path="/">
              <AgentRulesPage />
            </RequirePwaPermission>
          }
        />
        <Route path="notifications" element={<RequirePwaPermission path="/notifications"><NotificationsPage /></RequirePwaPermission>} />
        <Route path="conversations" element={<RequirePwaPermission path="/conversations"><ChatsLayout /></RequirePwaPermission>}>
          <Route index element={<ConversationListPage />} />
          <Route path=":id" element={<ConversationThreadPage />} />
        </Route>
        <Route path="calendar/reservas/:date" element={<RequirePwaPermission path="/calendar"><BookingsListPage /></RequirePwaPermission>} />
        <Route path="calendar" element={<RequirePwaPermission path="/calendar"><CalendarPage /></RequirePwaPermission>} />
        <Route path="reservas" element={<RequirePwaPermission path="/reservas"><BookingsSearchPage /></RequirePwaPermission>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  useLockAppViewportHeight();

  return (
    <div className="pwa-viewport">
      <PwaThemeProvider>
        <SessionProvider>
          <BrowserRouter>
            <PwaRouteSync />
            <div className="pwa-viewport__routes">
              <AppRoutes />
            </div>
          </BrowserRouter>
        </SessionProvider>
      </PwaThemeProvider>
    </div>
  );
}
